import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Link, useNavigate } from 'react-router-dom'
import { useGuild } from '../config/context/guild'
import { cn } from '@/lib/utils'
import { useHasSidebar } from '../hooks/useHasSidebar'
import { useLayoutEffect, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import {
  filterChildrenByAccess,
  filterMenuItemsByAccess,
  isMenuPathActive,
  MenuItem,
} from './menuAccess'

export type { MenuItem }

type OverflowMenuItem = Omit<MenuItem, 'children' | 'width'> & {
  width?: number
}
type OverflowMenuEntry =
  | {
      type: 'link'
      item: OverflowMenuItem
    }
  | {
      type: 'group'
      parent: MenuItem
      children: OverflowMenuItem[]
    }

export type MenuItemProps = {
  menuItems: MenuItem[]
  placement: 'subheader' | 'header'
}

const OVERFLOW_MENU_WIDTH = 88
const CUSTOM_LABEL_PADDING_WIDTH = 32
const DROPDOWN_CHEVRON_WIDTH = 16

const EntityMenu = ({ menuItems, placement }: MenuItemProps) => {
  const { teamReference, guild } = useGuild()
  const hasSidebar = useHasSidebar()
  const renderAsSidebarMenu = hasSidebar && placement === 'header'
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number>()
  const currentPath = window.location.pathname
  const hasHover = useMedia('(hover: hover) and (pointer: fine)', true)
  const navigate = useNavigate()

  const itemTriggerProps = (path: string) =>
    hasHover ? { onClick: () => navigate(path) } : {}

  const overflowTriggerProps = hasHover
    ? { onClick: (e: React.MouseEvent) => e.preventDefault() }
    : {}

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateContainerWidth = () => setContainerWidth(container.offsetWidth)
    updateContainerWidth()

    const resizeObserver = new ResizeObserver(updateContainerWidth)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  const pathIsActive = (path: string, exact?: boolean) =>
    isMenuPathActive(currentPath, path, exact)

  const menuItemsWithAccess = filterMenuItemsByAccess(
    menuItems,
    guild,
    teamReference,
  )
  const childrenWithAccessFor = (item: MenuItem) =>
    filterChildrenByAccess(item, guild, teamReference)
  const estimatedItemWidth = (item: MenuItem) => {
    const childrenWithAccess = childrenWithAccessFor(item)
    const childWidth =
      childrenWithAccess.length === 1
        ? childrenWithAccess[0].width ?? item.width
        : 0
    const customLabelWidth =
      typeof item.name === 'string' ? 0 : CUSTOM_LABEL_PADDING_WIDTH
    const dropdownWidth =
      childrenWithAccess.length > 1 ? DROPDOWN_CHEVRON_WIDTH : 0

    return Math.max(item.width, childWidth) + customLabelWidth + dropdownWidth
  }

  const fitItemsWithinWidth = (
    items: typeof menuItems,
    maxWidth: number,
    reservedWidth = 0,
  ) => {
    if (renderAsSidebarMenu) {
      return items
    }

    const availableWidth = Math.max(maxWidth - reservedWidth, 0)
    let totalWidth = 0
    const visibleItems: typeof menuItems = []

    for (const item of items) {
      const itemWidth = estimatedItemWidth(item)
      if (totalWidth + itemWidth > availableWidth) break
      visibleItems.push(item)
      totalWidth += itemWidth
    }

    return visibleItems
  }

  const availableWidth = containerWidth ?? window.innerWidth

  let visibleMenuItems = fitItemsWithinWidth(
    menuItemsWithAccess,
    availableWidth,
  )

  let invisibleMenuItems = menuItemsWithAccess.slice(visibleMenuItems.length)
  if (invisibleMenuItems.length > 0) {
    visibleMenuItems = fitItemsWithinWidth(
      menuItemsWithAccess,
      availableWidth,
      OVERFLOW_MENU_WIDTH,
    )
    invisibleMenuItems = menuItemsWithAccess.slice(visibleMenuItems.length)
  }

  if (invisibleMenuItems.length === 1 && visibleMenuItems.length > 0) {
    invisibleMenuItems = [
      visibleMenuItems.pop() as typeof menuItems[number],
      ...invisibleMenuItems,
    ]
  }
  const overflowMenuEntries = invisibleMenuItems.map<OverflowMenuEntry>(
    (item) => {
      const childrenWithAccess = childrenWithAccessFor(item)

      if (!childrenWithAccess.length) return { type: 'link', item }

      return {
        type: 'group',
        parent: item,
        children: childrenWithAccess,
      }
    },
  )

  return (
    <div
      ref={containerRef}
      className={cn(
        renderAsSidebarMenu
          ? 'flex flex-col w-full'
          : 'flex min-w-0 flex-1 items-center',
      )}
    >
      {visibleMenuItems.map((item) => {
        const childrenWithAccess = childrenWithAccessFor(item)
        const hasActiveChild = childrenWithAccess.some((child) => {
          return pathIsActive(child.path, child.exact)
        })
        const isItemActive = pathIsActive(item.path, item.exact)

        const target =
          childrenWithAccess.length === 1 ? childrenWithAccess[0] : item

        return (
          <div key={item.path}>
            {childrenWithAccess.length > 1 && !renderAsSidebarMenu ? (
              <NavigationMenu delayDuration={50}>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      {...itemTriggerProps(item.path)}
                      className={cn(
                        'cursor-default pr-2 rounded-none',
                        isItemActive || hasActiveChild
                          ? 'border-b-foreground bg-card'
                          : '',
                        placement === 'header'
                          ? renderAsSidebarMenu
                            ? 'bg-transparent py-0'
                            : 'h-14 bg-transparent'
                          : 'bg-background mb-[-2px] border-b-2',
                      )}
                    >
                      {item.name}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div>
                        {childrenWithAccess.map((child) => {
                          return (
                            <NavigationMenuLink
                              key={child.path}
                              active={pathIsActive(child.path, child.exact)}
                              className={`${navigationMenuTriggerStyle()} hover:bg-accent rounded-none last:rounded-b-md min-w-full data-active:text-primary`}
                              asChild
                            >
                              <Link
                                to={child.path}
                                className='whitespace-nowrap'
                              >
                                {child.name}
                              </Link>
                            </NavigationMenuLink>
                          )
                        })}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            ) : (
              <Link
                to={target.path}
                data-active={
                  pathIsActive(target.path, target.exact) || undefined
                }
                className={cn(
                  navigationMenuTriggerStyle(),
                  'bg-transparent rounded-none whitespace-nowrap',
                  placement === 'header'
                    ? renderAsSidebarMenu
                      ? 'h-9 border my-1 rounded-md bg-card-nested data-active:text-primary hover:text-accent-foreground transition-colors w-full justify-start'
                      : 'h-14 data-active:text-primary hover:text-accent-foreground transition-colors'
                    : 'mb-[-2px] border-b-2 border-b-transparent hover:border-b-border data-active:border-b-foreground',
                )}
              >
                {target.name}
              </Link>
            )}
          </div>
        )
      })}

      {invisibleMenuItems.length > 0 ? (
        <NavigationMenu
          delayDuration={50}
          viewportClassName='left-auto right-0'
        >
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuTrigger
                {...overflowTriggerProps}
                className={cn(
                  'cursor-default pr-2 rounded-none',
                  placement === 'header'
                    ? 'h-14 bg-transparent'
                    : 'bg-background mb-[-2px] border-b-2',
                )}
              >
                {visibleMenuItems.length > 0 ? 'More' : 'Menu'}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className='flex flex-col'>
                  {overflowMenuEntries.map((entry) => {
                    if (entry.type === 'group') {
                      return (
                        <div
                          key={entry.parent.path}
                          className='border-y first:border-t-0'
                        >
                          <div className='px-4 py-1.5 text-sm font-medium'>
                            {entry.parent.name}
                          </div>

                          {entry.children.map((child) => (
                            <NavigationMenuLink
                              key={child.path}
                              active={pathIsActive(child.path, child.exact)}
                              className={`${navigationMenuTriggerStyle()} rounded-none last:rounded-b-md min-w-full justify-start pl-6 whitespace-nowrap`}
                              asChild
                            >
                              <Link to={child.path}>{child.name}</Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      )
                    }

                    return (
                      <NavigationMenuLink
                        key={entry.item.path}
                        active={pathIsActive(entry.item.path, entry.item.exact)}
                        className={`${navigationMenuTriggerStyle()} rounded-none last:rounded-b-md min-w-full justify-start whitespace-nowrap`}
                        asChild
                      >
                        <Link to={entry.item.path}>{entry.item.name}</Link>
                      </NavigationMenuLink>
                    )
                  })}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      ) : null}
    </div>
  )
}

export default EntityMenu
